import { beginGoogleSignInAttempt, clearGoogleSignInAttempt, flushGoogleSignInOutcomes, getGoogleCallbackAttemptId, getGoogleSignInAttemptId, trackGoogleSignInOutcome, withGoogleSignInAttempt } from '@/utils/googleSignInTracking';
import { User, Session, type EmailOtpType, isAuthRetryableFetchError } from '@supabase/supabase-js';
import {
  createSignupRef,
  getPendingSignupRef,
  savePendingSignupEmail,
} from '@/utils/pendingSignupEmail';
import { createContext, startTransition, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { supabase } from '@/integrations/supabase/client';
import { env } from '@/lib/env';
import { usePostHogIdentity, waitForAnalyticsIdentity } from '@/lib/analytics/posthog';
import { hasProductAnalyticsOptOut } from '@/utils/productAnalyticsConsent';

import { BusinessEvents, setTrackingUserId } from '@/utils/trackingManager';
import { persistUserAcquisitionToServer } from '@/utils/persistUserAcquisition';
import {
  isNativeBillingEnabled,
  syncNativeCustomerAttributes,
} from '@/services/nativeBilling';
import { signInWithNativeApple } from '@/utils/nativeAppleSignIn';
import {
  isNativeGoogleSignInCanceled,
  isNativeGoogleSignInConfigured,
  signInWithNativeGoogle,
} from '@/utils/nativeGoogleSignIn';
import { signInWithWebApple } from '@/utils/webAppleSignIn';
import { buildExternalWebUrl } from '@/utils/publicShareUrls';
import { getAuthDiagnosticErrorMessage, trackAuthDiagnosticEvent } from '@/utils/authDiagnostics';
import {
  normalizeSafeSameOriginPath,
  sanitizeAnalyticsEventData,
  shouldSuppressSensitiveAuthTelemetry,
} from '@/utils/urlPrivacy';
import {
  clearNativeAccountCompletion,
  isAnonymousUser,
} from '@/utils/nativeAccountCompletion';
import { clearCheckoutAttributionContext } from '@/utils/checkoutAttribution';

interface UserProfile {
  id: string;
  auth_id: string;
  email: string | null;
  display_name: string;
  first_name: string | null;
  last_name: string | null;
  slug: string | null;
  full_name: string | null;
  nickname: string | null;
  avatar_url: string | null;
  bio: string | null;
  roles: string[];
  is_admin: boolean;
  account_status: 'active' | 'suspended' | 'pending';
  is_suspended?: boolean | null;
  subscription_tier: 'free' | 'premium' | 'trialing' | 'lifetime';
  profile_visibility: 'public' | 'private';
  show_on_leaderboard: boolean;
  show_on_map: boolean;
  city: string | null;
  state: string | null;
  country: string | null;
  hobbies: string[] | null;
  admin_notes: string | null;
  last_login: string | null;
  created_at: string;
  updated_at: string;
  // Migration fields (Story 11.1)
  migration_status?: string | null;
  migration_source?: string | null;
  lifetime_member?: boolean;
  grace_period_end?: string | null;
  // Onboarding fields (Epic 7)
  onboarding_completed_at?: string | null;
  /** Set when someone arrives through a Journey link (record_direct_journey_entry); skips onboarding. */
  onboarding_skipped_at?: string | null;
  /** True when onboarding_skipped_at couldn't be read; never treat that as "not skipped" on Journey paths. */
  onboarding_skip_unknown?: boolean;
  onboarding_offer_reached_at?: string | null;
  onboarding_offer_decided_at?: string | null;
  onboarding_intent?: 'journal_consistently' | 'journal_with_others' | 'curious' | 'friend_invited' | null;
  interests?: unknown;
  community_profile_set_at?: string | null;
  community_visibility_enforced_at?: string | null;
  community_visibility_chosen_at?: string | null;
  /** Stored Party Animal (profiles.party_animal): the avatar without a photo. */
  party_animal?: string | null;
  first_event_modal_seen?: boolean;
  first_event_completed_at?: string | null;
  first_prompt_tooltip_seen?: boolean;
  timezone?: string | null;
}

type AnonymousProviderConnectionResult = {
  error: unknown;
  connected: boolean;
  signedInExistingAccount?: boolean;
};

type AnonymousProviderConnectionOptions = {
  allowExistingAccountSignIn?: boolean;
};

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  loading: boolean;
  isLoading: boolean;
  profileLoading: boolean;
  profileLoadFailed: boolean;
  isRecovering: boolean;
  /**
   * True when the startup check could not read the stored session, even after
   * one retry (slow or failing network). The member may still be signed in on
   * this device, so do not treat the null user as a new visitor.
   */
  authStartupFailed: boolean;
  /** Runs the startup check again after authStartupFailed. */
  retryAuthStartup: () => void;
  startAnonymousSession: () => Promise<{ error: any }>;
  connectAnonymousWithProvider: (
    provider: 'google' | 'apple',
    redirectPath?: string,
    options?: AnonymousProviderConnectionOptions,
  ) => Promise<AnonymousProviderConnectionResult>;
  requestAnonymousEmailVerification: (
    email: string,
    redirectPath?: string,
  ) => Promise<{
    error: any;
    connected?: boolean;
    verificationRequired?: boolean;
  }>;
  verifyAnonymousEmail: (email: string, token: string) => Promise<{ error: any }>;
  refreshAnonymousEmailVerification: () => Promise<{
    error: any;
    connected: boolean;
  }>;
  requestEmailSignInCode: (
    email: string,
    redirectPath?: string,
  ) => Promise<{ error: any }>;
  verifyEmailSignInCode: (email: string, token: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (
    email: string,
    password: string,
    name?: SignUpNameInput,
    redirectPath?: string
  ) => Promise<{ error: any; isExistingAccount?: boolean; hasSession?: boolean }>;
  signInWithGoogle: (redirectPath?: string) => Promise<{ error: any }>;
  signInWithApple: (redirectPath?: string) => Promise<{ error: any }>;
  resendVerificationEmail: (email: string, redirectPath?: string) => Promise<{ error: any }>;
  signOut: () => Promise<{ error: any }>;
  resetPassword: (email: string, redirectPath?: string) => Promise<{ error: any }>;
  updateEmail: (email: string) => Promise<{ error: any }>;
  updatePassword: (password: string) => Promise<{ error: any }>;
  refreshProfile: () => Promise<void>;
}

interface SignUpNameInput {
  firstName?: string;
  lastName?: string;
  fullName?: string;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

const DEFAULT_POST_AUTH_PATH = '/home';
const DEFAULT_NATIVE_AUTH_REDIRECT_URL = 'com.pevr2wv0gtxs.p3gfapng2app://auth/callback';
const DEFAULT_WEB_AUTH_RELAY_URL = 'https://journalparty.com/auth/native-callback';
const DEFAULT_WEB_AUTH_CALLBACK_PATH = '/auth/callback';
const TRUSTED_WEB_AUTH_CALLBACK_ORIGINS = new Set([
  'https://journalparty.com',
  'https://www.journalparty.com',
  'https://reset.journalparty.com',
]);
export const NATIVE_OAUTH_ERROR_STORAGE_KEY = 'jp_native_oauth_error';
export const NATIVE_AUTH_FLOW_STORAGE_KEY = 'jp_native_auth_flow';
const TRACKED_SIGNUP_STORAGE_KEY_PREFIX = 'jp_tracked_signup:';
const PENDING_USER_ACQUISITION_STORAGE_KEY = 'jp_pending_user_acquisition_id';
const NATIVE_AUTH_FLOW_MAX_AGE_MS = 15 * 60 * 1000;
const AUTH_STARTUP_TIMEOUT_MS = 12000;
const AUTH_STARTUP_TIMEOUT_MESSAGE =
  'Authentication is taking too long to respond. Please check your connection and try again.';

function normalizeAuthEmail(email: string): string {
  return email.trim().toLowerCase();
}

function isNewEmailSignupUser(user: User | null): boolean {
  if (!user) return false;

  const identities = user.identities;
  return !Array.isArray(identities) || identities.length > 0;
}

function hasTrackedSignup(userId: string): boolean {
  if (typeof window === 'undefined') return false;

  try {
    return window.localStorage.getItem(`${TRACKED_SIGNUP_STORAGE_KEY_PREFIX}${userId}`) === '1';
  } catch {
    return false;
  }
}

function markSignupTracked(userId: string): void {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(`${TRACKED_SIGNUP_STORAGE_KEY_PREFIX}${userId}`, '1');
  } catch {
    // Ignore storage failures (private mode / browser restrictions).
  }
}

// A Google or Apple account created this recently counts as a new account.
const OAUTH_SIGNUP_WINDOW_MS = 5 * 60 * 1000;

// A native guest connecting Google or Apple through the browser fallback
// (startNativeOAuthProviderLink). Saved before the browser opens so the
// callback's SIGNED_IN, or the reload after it, still knows this account
// creation is a guest upgrade.
const NATIVE_IDENTITY_LINK_STORAGE_KEY = 'jp_native_identity_link';

type PendingIdentityLink = { userId: string; provider: 'google' | 'apple'; startedAt: number };

function markPendingIdentityLink(userId: string, provider: 'google' | 'apple'): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      NATIVE_IDENTITY_LINK_STORAGE_KEY,
      JSON.stringify({ userId, provider, startedAt: Date.now() } satisfies PendingIdentityLink)
    );
  } catch {
    // Without storage the browserFinished handler still sends guest_upgrade.
  }
}

function clearPendingIdentityLink(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(NATIVE_IDENTITY_LINK_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
}

function readPendingIdentityLink(userId: string): PendingIdentityLink | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(NATIVE_IDENTITY_LINK_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingIdentityLink>;
    const fresh =
      typeof parsed.startedAt === 'number' &&
      parsed.startedAt <= Date.now() &&
      Date.now() - parsed.startedAt <= NATIVE_AUTH_FLOW_MAX_AGE_MS;
    if (
      !fresh ||
      parsed.userId !== userId ||
      (parsed.provider !== 'google' && parsed.provider !== 'apple')
    ) {
      return null;
    }
    return parsed as PendingIdentityLink;
  } catch {
    return null;
  }
}

// Accounts whose sign_up decision is in flight, or was sent, on this page.
// Covers SIGNED_IN and the startup check seeing the same new account, and
// storage that cannot keep the per-browser mark.
// The longest the browser-close path waits for sign_up before navigating.
const SIGNUP_BEFORE_NAVIGATION_MAX_MS = 4000;
// user_metadata key for the SHA-256 of the email signup proof; track-event
// reads the same literal (supabase/functions/track-event/emailSignupClaim.ts).
// Only the hash is stored: a repeat signUp for an unconfirmed email returns
// the existing user, user_metadata included, to anyone who knows the email.
export const SIGNUP_PROOF_HASH_METADATA_KEY = 'jp_signup_proof_sha256';

function createRandomUuidV4(): string | undefined {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  if (typeof crypto.getRandomValues !== 'function') return undefined;
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

async function sha256Hex(value: string): Promise<string> {
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value)));
  return Array.from(digest, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function canHashSignupProof(): boolean {
  return typeof crypto !== 'undefined' && typeof crypto.subtle?.digest === 'function';
}

/**
 * A fresh email signup proof: a random v4 UUID this browser keeps and sends
 * with the sign_up, plus its SHA-256 for user_metadata. crypto.randomUUID
 * needs iOS 15.4 and the app still supports iOS 15.0, so older WebViews build
 * the UUID from getRandomValues. Resolves to undefined rather than
 * rejecting: signUp must never fail over analytics, and without a proof the
 * sign_up is stored unlinked.
 */
export async function createSignupProof(): Promise<SignupProof | undefined> {
  try {
    if (!canHashSignupProof()) return undefined;
    const proof = createRandomUuidV4()?.toLowerCase();
    if (!proof) return undefined;
    return { proof, proofHash: await sha256Hex(proof) };
  } catch {
    return undefined;
  }
}

/**
 * The proof for this email's signup: the pending one when there is one,
 * else a new one. Reading, creating and saving happen in one synchronous
 * step, and the proof used is whatever storage holds afterwards, so two tabs
 * submitting the same email at once settle on one proof before either
 * request goes out.
 */
async function resolveSignupProof(email: string): Promise<SignupProof | undefined> {
  try {
    if (!canHashSignupProof()) return undefined;
    let proof = readPendingSignupProof(email);
    if (!proof) {
      const fresh = createRandomUuidV4()?.toLowerCase();
      if (!fresh) return undefined;
      savePendingSignupProof(email, fresh);
      proof = readPendingSignupProof(email) ?? fresh;
    }
    return { proof, proofHash: await sha256Hex(proof) };
  } catch {
    return undefined;
  }
}

// Proofs of email signups whose sign_up has not been sent yet, one per email
// (separate tabs can sign up different emails). A resubmitted form reuses its
// email's proof: Supabase keeps the first proof's hash on an unconfirmed
// account, so a new proof could never link. Kept as long as track-event
// accepts the claim (10 minutes after the account was created).
const PENDING_SIGNUP_PROOFS_STORAGE_KEY = 'jp_pending_signup_proofs';
const PENDING_SIGNUP_PROOF_MAX_AGE_MS = 10 * 60 * 1000;

type SignupProof = { proof: string; proofHash: string };
type PendingSignupProofs = Record<string, { proof: string; savedAt: number }>;

function readPendingSignupProofs(): PendingSignupProofs {
  try {
    const raw = window.localStorage.getItem(PENDING_SIGNUP_PROOFS_STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    if (!parsed || typeof parsed !== 'object') return {};
    const now = Date.now();
    const fresh: PendingSignupProofs = {};
    for (const [email, entry] of Object.entries(parsed as Record<string, Partial<{ proof: string; savedAt: number }>>)) {
      if (
        typeof entry?.proof === 'string' &&
        typeof entry.savedAt === 'number' &&
        entry.savedAt <= now &&
        now - entry.savedAt <= PENDING_SIGNUP_PROOF_MAX_AGE_MS
      ) {
        fresh[email] = { proof: entry.proof, savedAt: entry.savedAt };
      }
    }
    return fresh;
  } catch {
    return {};
  }
}

function writePendingSignupProofs(proofs: PendingSignupProofs): void {
  try {
    if (Object.keys(proofs).length === 0) {
      window.localStorage.removeItem(PENDING_SIGNUP_PROOFS_STORAGE_KEY);
    } else {
      window.localStorage.setItem(PENDING_SIGNUP_PROOFS_STORAGE_KEY, JSON.stringify(proofs));
    }
  } catch {
    // Without storage a resubmitted form sends a new proof and stays unlinked.
  }
}

function readPendingSignupProof(email: string): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return readPendingSignupProofs()[email]?.proof;
}

function savePendingSignupProof(email: string, proof: string): void {
  if (typeof window === 'undefined') return;
  const proofs = readPendingSignupProofs();
  proofs[email] = { proof, savedAt: Date.now() };
  writePendingSignupProofs(proofs);
}

/** Clears this email's proof, and only if it is still the one that was sent. */
function clearPendingSignupProof(email: string, proof: string): void {
  if (typeof window === 'undefined') return;
  const proofs = readPendingSignupProofs();
  if (proofs[email]?.proof !== proof) return;
  delete proofs[email];
  writePendingSignupProofs(proofs);
}

const signupsInFlight = new Map<string, Promise<boolean>>();
const signupsSentThisPage = new Set<string>();

/** Test-only: forget the in-memory sign_up state between tests. */
export function __resetSignupTrackingForTests(): void {
  signupsInFlight.clear();
  signupsSentThisPage.clear();
}

/**
 * The one gate every sign_up goes through (email signups, OAuth redirects,
 * native Google and Apple accounts, native guest upgrades):
 * 1. skip on an auth callback page (telemetry is off there; the reload into
 *    the app retries);
 * 2. wait for the sign-in check of the account's analytics opt-out
 *    (waitForAnalyticsIdentity) and send nothing while it is pending or after
 *    it failed; an opt-out made on another device is applied to this device
 *    by that check, so the device choice is read after it. An email signup
 *    (createdNow) skips the wait: supabase.auth.signUp just created the
 *    account, which has had no session to set an opt-out with, and while
 *    email confirmation is pending there is no sign-in check to wait for.
 *    The device choice is still read;
 * 3. send server first with one event id per account (serverFirst), so
 *    track-event decides before any client destination fires and a second
 *    device sends nothing client-side;
 * 4. mark the account only after delivery settles and track-event did not
 *    report a failure, so a skipped, cut-off or unstored attempt can retry
 *    later.
 * Returns true when the event was sent.
 */
type GuardedSignup = {
  userId: string;
  method: 'email' | 'google' | 'apple';
  email?: string | null;
  flow?: 'guest_upgrade';
  firstName?: string;
  lastName?: string;
  createdNow?: boolean;
  signupProof?: string;
};

function sendGuardedSignup(args: GuardedSignup): Promise<boolean> {
  const { userId } = args;
  const alreadySent = () => hasTrackedSignup(userId) || signupsSentThisPage.has(userId);
  // A second caller shares the delivery already running, so it can wait on it.
  const inFlight = signupsInFlight.get(userId);
  if (inFlight) return inFlight;
  if (alreadySent()) return Promise.resolve(false);
  if (shouldSuppressSensitiveAuthTelemetry()) return Promise.resolve(false);

  const delivery = deliverGuardedSignup(args, alreadySent).finally(() => {
    signupsInFlight.delete(userId);
  });
  signupsInFlight.set(userId, delivery);
  return delivery;
}

async function deliverGuardedSignup(
  args: GuardedSignup,
  alreadySent: () => boolean,
): Promise<boolean> {
  const { userId } = args;
  {
    if (!args.createdNow) {
      const identity = await waitForAnalyticsIdentity(userId);
      if (identity !== 'clear') return false;
    }
    if (hasProductAnalyticsOptOut()) return false;
    if (alreadySent()) return false;
    // Marked only after delivery settles: a page that navigates away mid-request
    // (the browser-close path reloads at once) leaves no mark, so the next page
    // retries. A send track-event did not store (503, network error) leaves no
    // mark either, so a later attempt can still store the linked row.
    // signupsInFlight blocks a duplicate meanwhile; the server dedupes a retry
    // of a request that did land.
    let answer: Awaited<ReturnType<typeof BusinessEvents.signUp>>;
    try {
      answer = await BusinessEvents.signUp(
        args.method,
        args.email || undefined,
        {
          userId,
          ...(args.firstName ? { userFirstName: args.firstName } : {}),
          ...(args.lastName ? { userLastName: args.lastName } : {}),
          ...(args.signupProof ? { signupProof: args.signupProof } : {}),
        },
        args.flow ? { flow: args.flow, serverFirst: true } : { serverFirst: true },
      );
    } catch {
      return false;
    }
    if (answer === 'failed') return false;
    signupsSentThisPage.add(userId);
    markSignupTracked(userId);
    return true;
  }
}

/**
 * Every post-auth hard navigation goes through here: it lets any sign_up
 * delivery on this page (server-first send plus client destinations) finish
 * first, capped so sign-in never stalls. A macrotask yield first lets a
 * SIGNED_IN handler that was just notified register its delivery.
 */
async function navigateAfterAuth(nextPath: string): Promise<void> {
  if (typeof window === 'undefined') return;
  await new Promise<void>((resolve) => setTimeout(resolve, 0));
  if (signupsInFlight.size > 0) {
    await Promise.race([
      Promise.allSettled([...signupsInFlight.values()]),
      new Promise<void>((resolve) => setTimeout(resolve, SIGNUP_BEFORE_NAVIGATION_MAX_MS)),
    ]);
  }
  const currentPath = `${window.location.pathname}${window.location.search}`;
  if (currentPath !== nextPath) {
    window.location.href = nextPath;
  }
}

function hasLinkedIdentity(user: User, provider: 'google' | 'apple'): boolean {
  // Older sessions may not carry identities; the marker is user-bound then.
  if (!Array.isArray(user.identities)) return true;
  return user.identities.some((identity) => identity.provider === provider);
}

/**
 * sign_up for an account created through the Supabase OAuth redirect (web,
 * and the native browser fallback, including a guest linking Google or
 * Apple). Called on SIGNED_IN and on the startup session check, because a
 * web OAuth return lands on /auth/callback, where every analytics destination
 * stays off for the life of that page; the callback then reloads into the
 * app, and the startup check of that next page sends the event.
 *
 * A Google or Apple account counts as new when it was created in the last 5
 * minutes. A guest link counts when this device saved a fresh link marker for
 * this user and the user now has that provider's identity, whatever the
 * guest account's age (a cold callback after the app restarted has no
 * browserFinished listener left to send it).
 *
 * Native ID-token sign-ins use the server's isNewUser flag instead
 * (trackNativeIdTokenAuthSuccess); those accounts report provider 'email'.
 */
async function trackNewOAuthSignup(user: User): Promise<void> {
  if (isAnonymousUser(user)) return;
  const pendingLink = readPendingIdentityLink(user.id);
  const link = pendingLink && hasLinkedIdentity(user, pendingLink.provider) ? pendingLink : null;
  if (link) {
    if (hasTrackedSignup(user.id)) {
      clearPendingIdentityLink();
      return;
    }
    const sent = await sendGuardedSignup({
      userId: user.id,
      method: link.provider,
      email: user.email,
      flow: 'guest_upgrade',
    });
    if (sent) clearPendingIdentityLink();
    return;
  }

  const provider = user.app_metadata?.provider;
  if (provider !== 'google' && provider !== 'apple') return;
  const createdAtMs = user.created_at ? Date.parse(user.created_at) : Number.NaN;
  if (!Number.isFinite(createdAtMs) || Date.now() - createdAtMs >= OAUTH_SIGNUP_WINDOW_MS) {
    return;
  }
  await sendGuardedSignup({ userId: user.id, method: provider, email: user.email });
}

function setPendingUserAcquisition(userId: string): void {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(PENDING_USER_ACQUISITION_STORAGE_KEY, userId);
  } catch {
    // Ignore storage failures (private mode / browser restrictions).
  }
}

function getPendingUserAcquisition(): string | null {
  if (typeof window === 'undefined') return null;

  try {
    return window.localStorage.getItem(PENDING_USER_ACQUISITION_STORAGE_KEY);
  } catch {
    return null;
  }
}

function clearPendingUserAcquisition(userId: string): void {
  if (typeof window === 'undefined') return;

  try {
    if (window.localStorage.getItem(PENDING_USER_ACQUISITION_STORAGE_KEY) === userId) {
      window.localStorage.removeItem(PENDING_USER_ACQUISITION_STORAGE_KEY);
    }
  } catch {
    // Ignore storage failures.
  }
}

function normalizePostAuthPath(path?: string): string {
  return normalizeSafeSameOriginPath(path, DEFAULT_POST_AUTH_PATH);
}

function buildWebAuthCallbackUrl(
  path?: string,
  options?: { signup?: boolean; signupRef?: string | null }
): string {
  const nextPath = normalizePostAuthPath(path);
  if (!options?.signup) {
    return buildExternalWebUrl(DEFAULT_WEB_AUTH_CALLBACK_PATH, { next: nextPath });
  }
  return buildExternalWebUrl(DEFAULT_WEB_AUTH_CALLBACK_PATH, {
    next: nextPath,
    auth_flow: 'signup',
    signup_ref: options.signupRef ?? undefined,
  });
}

function getNativeAuthRedirectBaseUrl(): string {
  const configured = env.nativeAuthRedirectUrl;
  if (typeof configured === 'string' && configured.trim().length > 0) {
    return configured.trim();
  }
  return DEFAULT_NATIVE_AUTH_REDIRECT_URL;
}

function getWebAuthRelayBaseUrl(): string {
  const configured = env.webAuthRelayUrl;
  if (typeof configured === 'string' && configured.trim().length > 0) {
    return configured.trim();
  }
  return DEFAULT_WEB_AUTH_RELAY_URL;
}

function buildNativeAuthRelayUrl(path?: string): string {
  const nextPath = normalizePostAuthPath(path);
  const deepLink = getNativeAuthRedirectBaseUrl();
  try {
    const relayUrl = new URL(getWebAuthRelayBaseUrl());
    relayUrl.searchParams.set('next', nextPath);
    relayUrl.searchParams.set('deep_link', deepLink);
    return relayUrl.toString();
  } catch {
    return `${DEFAULT_WEB_AUTH_RELAY_URL}?next=${encodeURIComponent(nextPath)}&deep_link=${encodeURIComponent(deepLink)}`;
  }
}

function buildNativeEmailVerificationUrl(path?: string): string {
  const relayUrl = buildNativeAuthRelayUrl(path);
  try {
    const verificationUrl = new URL(relayUrl);
    verificationUrl.searchParams.set('email_confirmation', 'browser');
    return verificationUrl.toString();
  } catch {
    return `${relayUrl}&email_confirmation=browser`;
  }
}

function matchesNativeAuthRedirect(url: URL): boolean {
  const normalizedPath = url.pathname.replace(/\/+$/, '') || '/';
  if (
    url.protocol === 'https:' &&
    TRUSTED_WEB_AUTH_CALLBACK_ORIGINS.has(url.origin) &&
    normalizedPath === DEFAULT_WEB_AUTH_CALLBACK_PATH
  ) {
    return true;
  }

  try {
    const expected = new URL(getNativeAuthRedirectBaseUrl());
    const expectedPath = expected.pathname.replace(/\/+$/, '') || '/';
    return (
      url.protocol === expected.protocol &&
      url.hostname === expected.hostname &&
      normalizedPath === expectedPath
    );
  } catch {
    return false;
  }
}

function markNativeAuthFlowPending(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      NATIVE_AUTH_FLOW_STORAGE_KEY,
      JSON.stringify({ startedAt: Date.now() })
    );
  } catch {
    // The exact redirect boundary still protects users if storage is unavailable.
  }
}

function clearPendingNativeAuthFlow(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(NATIVE_AUTH_FLOW_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
}

function consumePendingNativeAuthFlow(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.localStorage.getItem(NATIVE_AUTH_FLOW_STORAGE_KEY);
    window.localStorage.removeItem(NATIVE_AUTH_FLOW_STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { startedAt?: unknown };
    return (
      typeof parsed.startedAt === 'number' &&
      parsed.startedAt <= Date.now() &&
      Date.now() - parsed.startedAt <= NATIVE_AUTH_FLOW_MAX_AGE_MS
    );
  } catch {
    clearPendingNativeAuthFlow();
    return false;
  }
}

function hasOAuthSessionCredentialPayload(url: URL): boolean {
  const params = getOAuthCallbackParams(url);
  return (
    params.has('code') ||
    params.has('access_token') ||
    params.has('refresh_token')
  );
}

function hasOAuthCallbackPayload(url: URL): boolean {
  const params = getOAuthCallbackParams(url);
  return (
    params.has('code') ||
    params.has('access_token') ||
    params.has('refresh_token') ||
    params.has('error') ||
    params.has('error_description') ||
    params.has('token_hash')
  );
}

function getOAuthCallbackParams(url: URL): URLSearchParams {
  const params = new URLSearchParams(url.search);
  const hashParams = new URLSearchParams(url.hash.startsWith('#') ? url.hash.slice(1) : url.hash);

  hashParams.forEach((value, key) => {
    if (!params.has(key)) {
      params.set(key, value);
    }
  });

  return params;
}

type OAuthProvider = 'google' | 'apple';

function isIdentityAlreadyLinkedError(error: unknown): boolean {
  const code =
    error && typeof error === 'object' && 'code' in error && typeof error.code === 'string'
      ? error.code.toLowerCase()
      : '';
  const message = getAuthDiagnosticErrorMessage(error).toLowerCase();

  return (
    code === 'identity_already_exists' ||
    message.includes('identity_already_exists') ||
    message.includes('identity already exists') ||
    message.includes('identity is already linked') ||
    message.includes('already linked to another user')
  );
}

function getOAuthQueryParams(provider: OAuthProvider): Record<string, string> | undefined {
  if (provider === 'google') {
    return {
      prompt: 'select_account',
    };
  }

  return undefined;
}

async function getFunctionErrorMessage(error: unknown): Promise<string | null> {
  if (!error || typeof error !== 'object') {
    return null;
  }

  const maybeContext = 'context' in error ? (error as { context?: Response }).context : undefined;
  if (!maybeContext || typeof maybeContext.text !== 'function' || maybeContext.bodyUsed) {
    return 'message' in error && typeof (error as { message?: unknown }).message === 'string'
      ? ((error as { message: string }).message || null)
      : null;
  }

  try {
    const rawText = await maybeContext.text();
    if (!rawText) return null;

    try {
      const parsed = JSON.parse(rawText) as { error?: unknown };
      return typeof parsed.error === 'string' ? parsed.error : rawText;
    } catch {
      return rawText;
    }
  } catch {
    return 'message' in error && typeof (error as { message?: unknown }).message === 'string'
      ? ((error as { message: string }).message || null)
      : null;
  }
}

async function applyAppleProfileMetadata(result: {
  givenName?: string | null;
  familyName?: string | null;
}) {
  if (!result.givenName && !result.familyName) {
    return;
  }

  await supabase.auth
    .updateUser({
      data: {
        full_name:
          [result.givenName, result.familyName].filter(Boolean).join(' ').trim() || undefined,
        first_name: result.givenName || undefined,
        last_name: result.familyName || undefined,
      },
    })
    .catch(() => undefined);
}

type AppleServerExchangeResult = {
  error: Error | null;
  email?: string;
  userId?: string;
  isNewUser: boolean;
};

async function completeAppleServerExchange(payload: {
  identityToken: string;
  authorizationCode?: string | null;
  nonce: string;
  email?: string | null;
  givenName?: string | null;
  familyName?: string | null;
  user?: string | null;
}): Promise<AppleServerExchangeResult> {
  const { data, error: exchangeError } = await supabase.functions.invoke('native-apple-auth', {
    body: payload,
  });

  if (exchangeError) {
    const message = await getFunctionErrorMessage(exchangeError);
    return { error: new Error(message || 'Apple sign-in could not be completed.'), isNewUser: false };
  }

  const responseData =
    data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  const hashedToken =
    typeof responseData.hashedToken === 'string' ? responseData.hashedToken : null;
  const verificationType =
    typeof responseData.verificationType === 'string' ? responseData.verificationType : null;

  if (typeof hashedToken !== 'string' || typeof verificationType !== 'string') {
    return { error: new Error('Apple sign-in could not be completed.'), isNewUser: false };
  }

  const { error } = await supabase.auth.verifyOtp({
    token_hash: hashedToken,
    type: verificationType as Extract<EmailOtpType, 'magiclink' | 'signup'>,
  });

  if (error) {
    return { error, isNewUser: false };
  }

  return {
    error: null,
    email: typeof responseData.email === 'string' ? responseData.email : undefined,
    userId: typeof responseData.userId === 'string' ? responseData.userId : undefined,
    isNewUser: responseData.isNewUser === true,
  };
}

function trackNativeIdTokenAuthSuccess(
  provider: 'apple' | 'google',
  exchange: AppleServerExchangeResult
) {
  if (exchange.error) return;

  if (exchange.userId) {
    setTrackingUserId(exchange.userId, exchange.email);
  }

  if (exchange.isNewUser) {
    if (exchange.userId) {
      if (!hasTrackedSignup(exchange.userId)) {
        scheduleUserAcquisitionPersistence(exchange.userId);
      }
      void sendGuardedSignup({ userId: exchange.userId, method: provider, email: exchange.email });
      return;
    }
    // Without an account id there is nothing to gate or dedupe on.
    void BusinessEvents.signUp(provider, exchange.email);
    return;
  }

  void BusinessEvents.login(provider);
}

function trackAppleAuthSuccess(exchange: AppleServerExchangeResult) {
  trackNativeIdTokenAuthSuccess('apple', exchange);
}

async function completeGoogleServerExchange(payload: {
  idToken: string;
  nonce?: string | null;
  existingOnly?: boolean;
  email?: string | null;
  givenName?: string | null;
  familyName?: string | null;
}): Promise<AppleServerExchangeResult> {
  const { data, error: exchangeError } = await supabase.functions.invoke('native-google-auth', {
    body: payload,
  });

  if (exchangeError) {
    const message = await getFunctionErrorMessage(exchangeError);
    return {
      error: new Error(message || 'Google sign-in could not be completed.'),
      isNewUser: false,
    };
  }

  const responseData =
    data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  const hashedToken =
    typeof responseData.hashedToken === 'string' ? responseData.hashedToken : null;
  const verificationType =
    typeof responseData.verificationType === 'string' ? responseData.verificationType : null;
  const isNewUser = responseData.isNewUser === true;

  if (typeof hashedToken !== 'string' || typeof verificationType !== 'string') {
    return { error: new Error('Google sign-in could not be completed.'), isNewUser: false };
  }

  if (payload.existingOnly && isNewUser) {
    return { error: new Error('Google sign-in did not match an existing account.'), isNewUser };
  }

  const { error } = await supabase.auth.verifyOtp({
    token_hash: hashedToken,
    type: verificationType as Extract<EmailOtpType, 'magiclink' | 'signup'>,
  });

  if (error) {
    return { error, isNewUser: false };
  }

  return {
    error: null,
    email: typeof responseData.email === 'string' ? responseData.email : undefined,
    userId: typeof responseData.userId === 'string' ? responseData.userId : undefined,
    isNewUser,
  };
}

function parseOAuthTokensFromCallback(url: URL): { accessToken: string; refreshToken: string } | null {
  const params = getOAuthCallbackParams(url);
  const accessToken = params.get('access_token');
  const refreshToken = params.get('refresh_token');

  if (!accessToken || !refreshToken) return null;

  return { accessToken, refreshToken };
}

function withAuthTimeout<T>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const timeoutPromise = new Promise<T>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(message)), timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timeoutId) clearTimeout(timeoutId);
  });
}

/**
 * True only when the auth server has definitively rejected the session: a 4xx
 * answer such as an invalid or missing refresh token, session_not_found or
 * user_not_found. Network failures (AuthRetryableFetchError), 5xx answers,
 * rate limits and our own startup timeout are not rejections: the member keeps
 * the cached session and supabase-js auto-refresh recovers it later.
 */
function isDefinitiveAuthRejection(error: unknown): boolean {
  if (!error || typeof error !== 'object' || isAuthRetryableFetchError(error)) return false;
  const status = (error as { status?: unknown }).status;
  return (
    typeof status === 'number' &&
    status >= 400 &&
    status < 500 &&
    status !== 408 &&
    status !== 429
  );
}

async function completeNativeOAuthCallback(callbackUrl: URL): Promise<void> {
  const callbackParams = getOAuthCallbackParams(callbackUrl);

  if (!hasOAuthCallbackPayload(callbackUrl)) {
    return;
  }

  const callbackError = callbackParams.get('error_description') || callbackParams.get('error');
  if (callbackError) {
    throw new Error('Sign-in could not be completed. Please try again.');
  }

  const code = callbackParams.get('code');
  if (code) {
    const { error } = await supabase.auth.exchangeCodeForSession(code);
    if (error) {
      throw error;
    }
    return;
  }

  if (callbackParams.has('access_token') || callbackParams.has('refresh_token')) {
    // Native OAuth is PKCE-only. Never accept a bearer-session pair from an
    // app deep link because it cannot be bound to the flow this device began.
    throw new Error('Sign-in could not be completed. Please try again.');
  }

  const tokenHash = callbackParams.get('token_hash');
  const otpType = callbackParams.get('type');
  const supportedOtpTypes: EmailOtpType[] = [
    'signup',
    'email',
    'recovery',
    'invite',
    'magiclink',
    'email_change',
  ];

  if (tokenHash && otpType && supportedOtpTypes.includes(otpType as EmailOtpType)) {
    const { error } = await supabase.auth.verifyOtp({
      token_hash: tokenHash,
      type: otpType as EmailOtpType,
    });
    if (error) {
      throw error;
    }
  }
}

async function setSentryUserLazy(payload: { id: string }) {
  try {
    const mod = await import('@/utils/sentry');
    mod.setSentryUser({ id: payload.id });
  } catch {
    // no-op if Sentry isn't available
  }
}

async function clearSentryUserLazy() {
  try {
    const mod = await import('@/utils/sentry');
    mod.clearSentryUser();
  } catch {
    // no-op if Sentry isn't available
  }
}

function persistNativeOAuthError(message: string) {
  if (typeof window === 'undefined') return;

  try {
    const sanitized = sanitizeAnalyticsEventData({ error_message: message }).error_message;
    const safeMessage =
      typeof sanitized === 'string' && sanitized !== '[redacted-sensitive-auth-data]'
        ? sanitized.slice(0, 500)
        : 'Sign-in could not be completed. Please try again.';
    window.sessionStorage.setItem(NATIVE_OAUTH_ERROR_STORAGE_KEY, safeMessage);
  } catch {
    // Ignore storage failures.
  }
}

function clearNativeOAuthError() {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.removeItem(NATIVE_OAUTH_ERROR_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
}

function trackNativeOAuthCallbackFailure(error: unknown, stage: string, googleAttemptId?: string) {
  const message = getAuthDiagnosticErrorMessage(error);
  trackGoogleSignInOutcome(message === AUTH_STARTUP_TIMEOUT_MESSAGE ? 'timed_out' : 'failed', 'callback', googleAttemptId);
  void trackAuthDiagnosticEvent('native_oauth_callback_failed', {
    stage,
    error_message: message,
    is_timeout: message === AUTH_STARTUP_TIMEOUT_MESSAGE,
  });
}

async function updateLastLogin(userId: string) {
  try {
    await supabase
      .from('profiles')
      .update({ last_login: new Date().toISOString() })
      .eq('user_id', userId);
  } catch (error) {
    console.error('Error updating last login:', error);
  }
}

// Auto-detect and update user timezone on login for localized email scheduling.
async function updateTimezone(userId: string) {
  try {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (browserTimezone) {
      await supabase
        .from('profiles')
        .update({ timezone: browserTimezone })
        .eq('user_id', userId);
    }
  } catch (error) {
    console.error('Error updating timezone:', error);
  }
}

async function persistUserAcquisition(userId: string): Promise<boolean> {
  // Server-side fill-empty upsert; also merges the signup-metadata snapshot
  // (magic-link flows) when this browser has no stored context. The pending
  // flag survives failures so the next SIGNED_IN retries; the acquisition
  // context itself lives 30 days.
  const persisted = await persistUserAcquisitionToServer();
  if (persisted) {
    clearPendingUserAcquisition(userId);
  }
  return persisted;
}

function scheduleUserAcquisitionPersistence(userId: string): void {
  setPendingUserAcquisition(userId);
  void persistUserAcquisition(userId);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileLoadFailed, setProfileLoadFailed] = useState(false);
  const profileRequestSequenceRef = useRef(0);
  const [isRecovering, setIsRecovering] = useState(false);
  const recoveryUserIdRef = useRef<string | null>(null);
  const [authStartupFailed, setAuthStartupFailed] = useState(false);
  const nativeBillingEnabled = isNativeBillingEnabled();
  const isNativeRuntime = Capacitor.isNativePlatform();
  const initialSessionResolvedRef = useRef(false);
  const authStateSequenceRef = useRef(0);
  // True from a failed startup check until the auth state is known again
  // (stays true while a retry runs).
  const authStartupFailedRef = useRef(false);
  const startupValidationRef = useRef<(() => Promise<void>) | null>(null);

  // PostHog: identify by Supabase user id on sign-in (and restored sessions),
  // reset on sign-out. Only the id is sent; email is used for the internal
  // test-user check inside the PostHog module.
  usePostHogIdentity(user, profile?.subscription_tier ?? null);

  const fetchUserProfile = useCallback(async (userId: string, shouldUpdateLogin = false) => {
    const requestSequence = ++profileRequestSequenceRef.current;
    const authSequence = authStateSequenceRef.current;
    const isCurrentRequest = () => requestSequence === profileRequestSequenceRef.current
      && authSequence === authStateSequenceRef.current;
    setProfileLoading(true);
    setProfileLoadFailed(false);

    // Login bookkeeping must not delay the profile needed for route access.
    if (shouldUpdateLogin) {
      void updateLastLogin(userId);
      void updateTimezone(userId);
    }

    const readProfile = async (): Promise<UserProfile> => {
      const { data, error } = await supabase.rpc('get_user_by_auth_id', {
        target_auth_id: userId
      });

      if (error) {
        throw error;
      }

      if (data && data.length > 0) {
        const apiData = data[0] as any;
        // get_user_by_auth_id doesn't return the Journey-entry onboarding skip.
        // A failed read is "unknown", never "not skipped": retry, then let
        // ProtectedRoute hold its onboarding redirect for Journey paths.
        // The same own-row read also returns the onboarding resume state the RPC
        // doesn't carry: the "Join the party" checkpoint and the saved answers.
        let journeyEntry: {
          onboarding_skipped_at: string | null;
          community_profile_set_at?: string | null;
          community_visibility_enforced_at?: string | null;
          community_visibility_chosen_at?: string | null;
          interests?: unknown;
          party_animal?: string | null;
        } | null = null;
        let skipUnknown = true;
        for (let attempt = 0; attempt < 3 && skipUnknown; attempt += 1) {
          const { data: entry, error: entryError } = await supabase.from('profiles')
            .select('onboarding_skipped_at, community_profile_set_at, community_visibility_enforced_at, community_visibility_chosen_at, interests, party_animal').eq('user_id', userId).maybeSingle();
          if (!entryError) { journeyEntry = entry; skipUnknown = false; }
        }
        const userData: UserProfile = {
          ...apiData,
          display_name: apiData.display_name || apiData.nickname || '',
          onboarding_skipped_at: journeyEntry?.onboarding_skipped_at ?? null,
          community_profile_set_at: journeyEntry?.community_profile_set_at ?? null,
          community_visibility_enforced_at: journeyEntry?.community_visibility_enforced_at ?? null,
          community_visibility_chosen_at: journeyEntry?.community_visibility_chosen_at ?? null,
          interests: apiData.interests ?? journeyEntry?.interests ?? null,
          party_animal: journeyEntry?.party_animal ?? null,
          onboarding_skip_unknown: skipUnknown,
          first_name: apiData.first_name ?? null,
          last_name: apiData.last_name ?? null,
          slug: apiData.slug ?? null,
          nickname: apiData.nickname ?? apiData.display_name ?? null,
        };
        return userData;
      }
      throw new Error('Account profile unavailable');
    };

    try {
      // Keep state changes outside the timed operation. A late response from
      // a timed-out read, retry, or signed-out account cannot restore access.
      const userData = await withAuthTimeout(readProfile(), 15000, 'Account profile load timed out');
      if (!isCurrentRequest()) return null;
      startTransition(() => {
        setProfile(userData);
        setProfileLoadFailed(false);
        setProfileLoading(false);
      });
      if (nativeBillingEnabled) {
        void syncNativeCustomerAttributes(userId, {
          email: userData.email,
          displayName: userData.display_name || userData.nickname || userData.full_name || null,
        }).catch((error) => {
          console.error('Error syncing RevenueCat customer attributes:', error);
        });
      }
      void setSentryUserLazy({ id: userId });
      return userData;
    } catch (error) {
      if (!isCurrentRequest()) return null;
      console.error('Error fetching profile:', error);
      startTransition(() => {
        setProfileLoadFailed(true);
        setProfileLoading(false);
      });
      return null;
    }
  }, [nativeBillingEnabled]);

  useEffect(() => {
    flushGoogleSignInOutcomes();
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // Startup validation owns the initial session. This cached SDK
        // snapshot can arrive after getUser and must not overwrite verified
        // setup metadata (or turn a failed stored-session read into sign-out).
        if (event === 'INITIAL_SESSION') return;

        authStateSequenceRef.current += 1;
        const authStateSequence = authStateSequenceRef.current;
        initialSessionResolvedRef.current = true;
        authStartupFailedRef.current = false;

        // Transitions: these updates can land while the lazy route's Suspense
        // boundary is still hydrating after SSR. A non-transition update there
        // makes React abandon the server HTML (minified error #421).
        startTransition(() => {
          setSession(session);
          setUser(session?.user ?? null);
          setAuthStartupFailed(false);

          // Refreshes and repeated sign-ins can arrive while a member types.
          // Keep recovery for this account until a successful save or sign-out.
          if (event === 'PASSWORD_RECOVERY') {
            recoveryUserIdRef.current = session?.user.id ?? null;
          } else if (!session || session.user.id !== recoveryUserIdRef.current) {
            recoveryUserIdRef.current = null;
          }
          setIsRecovering(Boolean(recoveryUserIdRef.current));
        });

        if (session?.user) {
          setProfileLoading(true);
          clearNativeOAuthError();
          setTrackingUserId(session.user.id, session.user.email);

          // Update last login only on sign-in events (not recovery)
          const shouldUpdateLogin = event === 'SIGNED_IN';

          const createdAtMs = session.user.created_at
            ? Date.parse(session.user.created_at)
            : Number.NaN;

          // Track OAuth signups here because completion returns through auth state.
          // Do not track OAuth logins from SIGNED_IN: Supabase may emit it when
          // an existing session is re-confirmed, which inflates GA4 login counts.
          if (event === 'SIGNED_IN') {
            void trackNewOAuthSignup(session.user);
          }

          // Acquisition persistence covers EVERY provider, unlike signup
          // analytics above: magic-link/OTP signups (email-first checkout)
          // arrive here as provider 'email' and previously never wrote a
          // user_acquisition row. The window matches the magic link's
          // 1-hour validity (supabase/config.toml otp_expiry), NOT the
          // 5-minute analytics window: a link opened 40 minutes later in a
          // different browser still has to consume its jp_acquisition
          // signup-metadata snapshot. The server upsert is idempotent, so
          // repeat sign-ins inside the hour are harmless.
          const isWithinAcquisitionWindow =
            Number.isFinite(createdAtMs) && Date.now() - createdAtMs < 60 * 60 * 1000;
          if (
            event === 'SIGNED_IN' &&
            !isAnonymousUser(session.user) &&
            isWithinAcquisitionWindow
          ) {
            scheduleUserAcquisitionPersistence(session.user.id);
          } else if (getPendingUserAcquisition() === session.user.id) {
            void persistUserAcquisition(session.user.id);
          }

          // Fetch user profile
          setTimeout(() => {
            if (authStateSequenceRef.current !== authStateSequence) return;
            // Supabase can re-emit cached SIGNED_IN on startup and supersede
            // startup validation. Verify pending setup metadata separately so
            // a completed save with a lost response does not force setup again.
            if (shouldUpdateLogin && session.user.user_metadata?.requires_password_setup === true
              && !session.user.user_metadata?.password_setup_completed_at) {
              void withAuthTimeout(supabase.auth.getUser(session.access_token), AUTH_STARTUP_TIMEOUT_MS, AUTH_STARTUP_TIMEOUT_MESSAGE)
                .then(({ data, error }) => {
                  if (error || !data.user || data.user.id !== session.user.id
                    || authStateSequenceRef.current !== authStateSequence) return;
                  const verifiedUser = data.user;
                  startTransition(() => {
                    setUser(verifiedUser);
                    setSession((current) => current?.user.id === verifiedUser.id
                      ? { ...current, user: verifiedUser } : current);
                  });
                }).catch(() => undefined);
            }
            void fetchUserProfile(session.user.id, shouldUpdateLogin);
          }, 0);
        } else {
          startTransition(() => {
            setProfile(null);
            setProfileLoadFailed(false);
            setProfileLoading(false);
          });
        }

        if (authStateSequenceRef.current === authStateSequence) {
          startTransition(() => {
            setLoading(false);
          });
        }
      }
    );

    // Check for an existing session and validate it so stale cached auth
    // does not masquerade as a live signed-in state. A timeout or network
    // failure is not a sign-out: the member keeps the cached session, and only
    // a definitive rejection from the auth server clears it.
    const validateStartupSession = async () => {
      const validationSequence = authStateSequenceRef.current;
      const isValidationStale = () => authStateSequenceRef.current !== validationSequence;
      let startupStep: 'get_session' | 'get_user' | 'refresh_session' = 'get_session';
      let sessionToKeep: Session | null = null;

      // getSession reads storage and only goes to the network to refresh an
      // expired access token. It returns an error with no session when that
      // refresh fails; supabase-js keeps the stored session unless the server
      // rejected it, so only a definitive rejection reads as signed out.
      const readStoredSession = async () => {
        const { data, error } = await withAuthTimeout(
          supabase.auth.getSession(),
          AUTH_STARTUP_TIMEOUT_MS,
          AUTH_STARTUP_TIMEOUT_MESSAGE
        );
        if (error && !isDefinitiveAuthRejection(error)) throw error;
        return data.session;
      };

      try {
        let cachedSession: Session | null;
        try {
          cachedSession = await readStoredSession();
        } catch {
          if (isValidationStale()) return;
          // One retry: a slow read usually means a stored session exists.
          cachedSession = await readStoredSession();
        }
        if (isValidationStale()) return;

        // All state flips below run inside startTransition: this initial
        // validation resolves while SSR'd routes are still hydrating, and a
        // non-transition update would force client rendering (error #421).
        if (!cachedSession?.user) {
          initialSessionResolvedRef.current = true;
          authStartupFailedRef.current = false;
          startTransition(() => {
            setSession(null);
            setUser(null);
            setProfileLoading(false);
            setLoading(false);
          });
          return;
        }

        startupStep = 'get_user';
        sessionToKeep = cachedSession;
        const { data: currentUserData, error: currentUserError } = await withAuthTimeout(
          supabase.auth.getUser(cachedSession.access_token),
          AUTH_STARTUP_TIMEOUT_MS,
          AUTH_STARTUP_TIMEOUT_MESSAGE
        );
        if (isValidationStale()) return;
        if (!currentUserError && currentUserData.user) {
          initialSessionResolvedRef.current = true;
          authStartupFailedRef.current = false;
          void trackNewOAuthSignup(currentUserData.user);
          // A completed password save may have lost its response before a
          // reload. Verified server metadata supersedes the cached setup flag.
          const validatedSession = { ...cachedSession, user: currentUserData.user };
          startTransition(() => {
            setSession(validatedSession);
            setUser(currentUserData.user);
            setProfileLoading(true);
            setLoading(false);
          });
          void fetchUserProfile(cachedSession.user.id);
          return;
        }
        // Only a definitive rejection means the access token is bad. A network
        // failure keeps the cached session (handled in the catch below).
        if (currentUserError && !isDefinitiveAuthRejection(currentUserError)) {
          throw currentUserError;
        }

        startupStep = 'refresh_session';
        const { data: refreshed, error: refreshError } = await withAuthTimeout(
          supabase.auth.refreshSession(),
          AUTH_STARTUP_TIMEOUT_MS,
          AUTH_STARTUP_TIMEOUT_MESSAGE
        );
        if (isValidationStale()) return;
        if (refreshError && !isDefinitiveAuthRejection(refreshError)) throw refreshError;
        const refreshedSession = refreshed.session;
        if (refreshError || !refreshedSession?.user) {
          initialSessionResolvedRef.current = true;
          authStartupFailedRef.current = false;
          await supabase.auth.signOut({ scope: 'local' }).catch(() => undefined);
          if (isValidationStale()) return;
          startTransition(() => {
            setSession(null);
            setUser(null);
            setProfile(null);
            setProfileLoadFailed(false);
            setProfileLoading(false);
            setLoading(false);
          });
          return;
        }

        initialSessionResolvedRef.current = true;
        authStartupFailedRef.current = false;
        void trackNewOAuthSignup(refreshedSession.user);
        startTransition(() => {
          setSession(refreshedSession);
          setUser(refreshedSession.user);
          setProfileLoading(true);
          setLoading(false);
        });
        void fetchUserProfile(refreshedSession.user.id);
      } catch (error) {
        if (isValidationStale()) return;
        console.error('Auth startup validation failed:', error);
        const message = getAuthDiagnosticErrorMessage(error);
        void trackAuthDiagnosticEvent(
          message === AUTH_STARTUP_TIMEOUT_MESSAGE ? 'auth_startup_timeout' : 'auth_startup_failed',
          {
            stage: 'startup_validation',
            error_message: message,
            is_timeout: message === AUTH_STARTUP_TIMEOUT_MESSAGE,
            startup_step: startupStep,
            kept_cached_session: Boolean(sessionToKeep),
          }
        );
        initialSessionResolvedRef.current = true;
        if (sessionToKeep) {
          // The auth server could not be reached in time. Keep the member
          // signed in with the cached session; supabase-js auto-refresh
          // recovers it, and a later definitive rejection arrives as SIGNED_OUT.
          const keptSession = sessionToKeep;
          authStartupFailedRef.current = false;
          startTransition(() => {
            setSession(keptSession);
            setUser(keptSession.user);
            setProfileLoading(true);
            setLoading(false);
          });
          void fetchUserProfile(keptSession.user.id);
          return;
        }
        // The stored session could not be read even after the retry. The
        // device may still hold the member's session, so flag the failure
        // instead of looking signed out: native launch must not replace it
        // with a new guest.
        authStartupFailedRef.current = true;
        startTransition(() => {
          setSession(null);
          setUser(null);
          setProfile(null);
          setProfileLoading(false);
          setAuthStartupFailed(true);
          setLoading(false);
        });
      }
    };
    startupValidationRef.current = validateStartupSession;
    void validateStartupSession();

    return () => {
      subscription.unsubscribe();
      profileRequestSequenceRef.current += 1;
    };
  }, [fetchUserProfile]);

  const retryAuthStartup = useCallback(() => {
    const validateStartupSession = startupValidationRef.current;
    if (!validateStartupSession) return;
    startTransition(() => {
      setAuthStartupFailed(false);
      setLoading(true);
    });
    void validateStartupSession();
  }, []);

  useEffect(() => {
    if (!user?.id || !profile?.id) return;

    let cancelled = false;

    const syncNativeSubscriptionState = async () => {
      try {
        // Read the token at call time instead of depending on it: access tokens
        // rotate roughly hourly and this sync only needs to run once per sign-in,
        // not on every refresh.
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        const accessToken = currentSession?.access_token;
        if (!accessToken || cancelled) return;

        const { error } = await supabase.functions.invoke('sync-native-subscription', {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: {},
        });

        if (error) {
          console.error('Error syncing native subscription:', error);
          return;
        }

        if (!cancelled) {
          await fetchUserProfile(user.id);
        }
      } catch (error) {
        console.error('Error syncing native subscription:', error);
      }
    };

    // Run this on web too. RevenueCat/App Store purchases are keyed by our
    // Supabase user id, so web sign-in can repair native-store access even
    // when the customer bought in the mobile app and never opens the app again.
    void syncNativeSubscriptionState();

    return () => {
      cancelled = true;
    };
  }, [user?.id, profile?.id, fetchUserProfile]);

  useEffect(() => {
    if (!isNativeRuntime) return;

    let cancelled = false;

    const handleNativeAuthRedirect = async (url: string): Promise<boolean> => {
      if (!url) return false;

      let callbackUrl: URL;
      try {
        callbackUrl = new URL(url);
      } catch {
        return false;
      }

      if (!matchesNativeAuthRedirect(callbackUrl)) return false;
      if (!hasOAuthCallbackPayload(callbackUrl)) return false;
      if (
        hasOAuthSessionCredentialPayload(callbackUrl) &&
        !consumePendingNativeAuthFlow()
      ) {
        return false;
      }

      const googleAttemptId = getGoogleCallbackAttemptId(getOAuthCallbackParams(callbackUrl));
      try {
        const callbackParams = getOAuthCallbackParams(callbackUrl);
        void trackAuthDiagnosticEvent('native_oauth_callback_received', {
          stage: 'native_oauth_callback',
          callback_has_code: callbackParams.has('code'),
          callback_has_tokens: Boolean(parseOAuthTokensFromCallback(callbackUrl)),
          callback_has_token_hash: callbackParams.has('token_hash'),
          callback_has_error: callbackParams.has('error'),
        });
        await completeNativeOAuthCallback(callbackUrl);
        trackGoogleSignInOutcome('completed', 'callback', googleAttemptId);
      } catch (error) {
        console.error('Error completing native OAuth flow:', error);
        trackNativeOAuthCallbackFailure(error, 'native_oauth_callback', googleAttemptId);
        const fallbackMessage = 'Sign-in could not be completed. Please try again.';
        persistNativeOAuthError(
          error instanceof Error && error.message ? error.message : fallbackMessage
        );
      } finally {
        await Browser.close().catch(() => undefined);
      }

      if (!cancelled) {
        const nextPath = normalizePostAuthPath(callbackUrl.searchParams.get('next') || undefined);
        await navigateAfterAuth(nextPath);
      }

      return true;
    };

    void (async () => {
      try {
        const launchUrl = await CapacitorApp.getLaunchUrl();
        if (!cancelled && launchUrl?.url) {
          await handleNativeAuthRedirect(launchUrl.url);
        }
      } catch (error) {
        void trackAuthDiagnosticEvent('native_oauth_launch_url_failed', {
          stage: 'native_oauth_launch_url_lookup',
          error_message: getAuthDiagnosticErrorMessage(error),
        });
        // Ignore startup URL lookup failures; live appUrlOpen still handles warm callbacks.
      }
    })();

    const listenerPromise = CapacitorApp.addListener('appUrlOpen', ({ url }) => {
      if (!url) return;
      void handleNativeAuthRedirect(url);
    });

    return () => {
      cancelled = true;
      void listenerPromise.then((listener) => listener.remove());
    };
  }, [isNativeRuntime]);

  const refreshProfile = useCallback(async () => {
    if (!user) return;
    await fetchUserProfile(user.id);
  }, [user, fetchUserProfile]);

  const startAnonymousSession = useCallback(async () => {
    clearNativeOAuthError();
    if (!isNativeRuntime) {
      return { error: new Error('Guest onboarding is only available in the mobile app.') };
    }

    const { error } = await supabase.auth.signInAnonymously({
      options: {
        data: {
          signup_source: 'native_guest_onboarding',
        },
      },
    });
    return { error };
  }, [isNativeRuntime]);

  const finalizeNativeGuestAccount = useCallback(async () => {
    const { error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError) return { error: refreshError };

    const { error } = await supabase.rpc('finalize_native_guest_account');
    return { error };
  }, []);

  const requestAnonymousEmailVerification = useCallback(async (
    email: string,
    redirectPath = '/complete-account',
  ) => {
    if (!user || !isAnonymousUser(user)) {
      return { error: new Error('This account is already connected to an email.') };
    }
    const normalizedEmail = normalizeAuthEmail(email);
    const redirectUrl = isNativeRuntime
      ? buildNativeEmailVerificationUrl(redirectPath)
      : buildWebAuthCallbackUrl(redirectPath);
    const { data, error } = await supabase.auth.updateUser(
      { email: normalizedEmail },
      { emailRedirectTo: redirectUrl },
    );
    if (error) return { error };

    // Supabase can either require an email-change OTP or immediately convert
    // the guest, depending on the live Auth confirmation policy. Finalize the
    // immediate path here instead of sending the UI to a code screen that will
    // be skipped as soon as the auth-state update arrives.
    if (!data.user || isAnonymousUser(data.user)) {
      return { error: null, connected: false, verificationRequired: true };
    }

    const finalized = await finalizeNativeGuestAccount();
    if (finalized.error) return { error: finalized.error };
    clearNativeAccountCompletion(data.user.id);
    scheduleUserAcquisitionPersistence(data.user.id);
    setTrackingUserId(data.user.id, normalizedEmail);
    void sendGuardedSignup({
      userId: data.user.id,
      method: 'email',
      email: normalizedEmail,
      flow: 'guest_upgrade',
    });
    return { error: null, connected: true, verificationRequired: false };
  }, [finalizeNativeGuestAccount, isNativeRuntime, user]);

  const verifyAnonymousEmail = useCallback(async (email: string, token: string) => {
    if (!user || !isAnonymousUser(user)) {
      return { error: new Error('This account is already connected to an email.') };
    }
    const normalizedEmail = normalizeAuthEmail(email);
    const { data, error } = await supabase.auth.verifyOtp({
      email: normalizedEmail,
      token: token.trim(),
      type: 'email_change',
    });

    if (!error && data.user) {
      const finalized = await finalizeNativeGuestAccount();
      if (finalized.error) return finalized;
      clearNativeAccountCompletion(data.user.id);
      scheduleUserAcquisitionPersistence(data.user.id);
      setTrackingUserId(data.user.id, normalizedEmail);
      void sendGuardedSignup({
        userId: data.user.id,
        method: 'email',
        email: normalizedEmail,
        flow: 'guest_upgrade',
      });
    }
    return { error };
  }, [finalizeNativeGuestAccount, user]);

  const refreshAnonymousEmailVerification = useCallback(async () => {
    if (!user) {
      return { error: new Error('No active account was found.'), connected: false };
    }
    if (!isAnonymousUser(user)) {
      return { error: null, connected: true };
    }

    const expectedUserId = user.id;
    const { data, error } = await supabase.auth.refreshSession();
    if (error) return { error, connected: false };

    const connectedUser = data.session?.user ?? null;
    if (!connectedUser || connectedUser.id !== expectedUserId) {
      return { error: new Error('The active account changed during email verification.'), connected: false };
    }
    if (isAnonymousUser(connectedUser)) {
      return { error: null, connected: false };
    }

    const { error: finalizeError } = await supabase.rpc('finalize_native_guest_account');
    if (finalizeError) return { error: finalizeError, connected: false };

    const normalizedEmail = normalizeAuthEmail(connectedUser.email ?? '');
    clearNativeAccountCompletion(connectedUser.id);
    scheduleUserAcquisitionPersistence(connectedUser.id);
    setTrackingUserId(connectedUser.id, normalizedEmail || undefined);
    void sendGuardedSignup({
      userId: connectedUser.id,
      method: 'email',
      email: normalizedEmail || undefined,
      flow: 'guest_upgrade',
    });
    return { error: null, connected: true };
  }, [user]);

  const requestEmailSignInCode = useCallback(async (
    email: string,
    redirectPath = DEFAULT_POST_AUTH_PATH,
  ) => {
    clearGoogleSignInAttempt();
    clearNativeOAuthError();
    const redirectUrl = isNativeRuntime
      ? buildNativeAuthRelayUrl(redirectPath)
      : buildWebAuthCallbackUrl(redirectPath);
    const { error } = await supabase.auth.signInWithOtp({
      email: normalizeAuthEmail(email),
      options: {
        shouldCreateUser: false,
        emailRedirectTo: redirectUrl,
      },
    });
    return { error };
  }, [isNativeRuntime]);

  const verifyEmailSignInCode = useCallback(async (email: string, token: string) => {
    clearNativeOAuthError();
    const { error } = await supabase.auth.verifyOtp({
      email: normalizeAuthEmail(email),
      token: token.trim(),
      type: 'email',
    });
    if (!error) {
      void BusinessEvents.login('email');
    }
    return { error };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    clearGoogleSignInAttempt();
    clearNativeOAuthError();
    const { error } = await supabase.auth.signInWithPassword({
      email: normalizeAuthEmail(email),
      password,
    });

    if (!error) {
      void BusinessEvents.login('email');
    }

    return { error };
  }, []);

  const signUp = useCallback(async (
    email: string,
    password: string,
    name?: SignUpNameInput,
    redirectPath?: string
  ) => {
    clearGoogleSignInAttempt();
    clearNativeOAuthError();
    const signupRef = isNativeRuntime ? null : createSignupRef();
    const redirectUrl = isNativeRuntime
      ? buildNativeAuthRelayUrl(redirectPath)
      : buildWebAuthCallbackUrl(redirectPath, { signup: true, signupRef });
    const normalizedEmail = normalizeAuthEmail(email);
    const firstName = name?.firstName?.trim() || '';
    const lastName = name?.lastName?.trim() || '';
    const fullName = name?.fullName?.trim() || [firstName, lastName].filter(Boolean).join(' ');
    const userData = fullName
      ? {
          full_name: fullName,
          first_name: firstName || fullName.split(/\s+/)[0],
          last_name: lastName || undefined,
        }
      : undefined;
    // A secret only this browser knows; the new account stores its hash.
    // track-event links the email sign_up to the account only when the
    // secret comes back, so a guessed user id and email cannot claim or probe
    // a new account.
    const signupProof = await resolveSignupProof(normalizedEmail);

    const { data, error } = await supabase.auth.signUp({
      email: normalizedEmail,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: signupProof
          ? { ...(userData ?? {}), [SIGNUP_PROOF_HASH_METADATA_KEY]: signupProof.proofHash }
          : userData,
      },
    });

    if (!error && isNewEmailSignupUser(data.user)) {
      if (signupRef) savePendingSignupEmail(normalizedEmail, signupRef);
      scheduleUserAcquisitionPersistence(data.user.id);
      setTrackingUserId(data.user.id, normalizedEmail);
      // Usually no session yet (email confirmation pending): track-event
      // links the event to this new account through its email signup claim.
      void sendGuardedSignup({
        userId: data.user.id,
        method: 'email',
        email: normalizedEmail,
        firstName: userData?.first_name,
        lastName: userData?.last_name,
        createdNow: true,
        signupProof: signupProof?.proof,
      }).then((sent) => {
        if (sent && signupProof) clearPendingSignupProof(normalizedEmail, signupProof.proof);
      });
    }

    // Supabase's email-enumeration protection returns a fake success with an
    // empty identities array when the email already belongs to an account
    // (for example one created through Google sign-in). No confirmation email
    // is sent in that case, so callers must not show a "check your email"
    // screen.
    const isExistingAccount = !error && !!data.user && !isNewEmailSignupUser(data.user);

    // When blocking email confirmation is disabled server-side, signUp returns
    // a live session and the user can go straight to onboarding instead of the
    // check-email screen.
    const hasSession = !error && !!data.session;

    return { error, isExistingAccount, hasSession };
  }, [isNativeRuntime]);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();

    // Clear Sentry user context on sign out (lazy)
    void clearSentryUserLazy();
    if (typeof window !== 'undefined') {
      clearNativeAccountCompletion();
      clearPendingIdentityLink();
      clearCheckoutAttributionContext();
      window.localStorage.removeItem('tracking_user_id');
      window.localStorage.removeItem('tracking_user_email');
    }

    return { error };
  }, []);

  const resetPassword = useCallback(async (email: string, redirectPath?: string) => {
    clearGoogleSignInAttempt();
    // Keep recovery mail on the central Transactional path until the Supabase
    // Auth Send Email Hook is activated. The compatibility endpoint generates
    // a one-time token_hash link and never changes the account password.
    const postRecoveryPath = normalizePostAuthPath(redirectPath);
    try {
      const { error } = await supabase.functions.invoke('request-temp-password', {
        body: {
          email: normalizeAuthEmail(email),
          redirectPath: postRecoveryPath,
        },
      });
      return { error };
    } catch (error) {
      return { error };
    }
  }, []);

  const startNativeOAuthProviderSignIn = useCallback(async (provider: OAuthProvider, redirectPath?: string) => {
    const googleAttemptId = provider === 'google' ? getGoogleSignInAttemptId() : undefined;
    clearPendingNativeAuthFlow();
    const redirectUrl = withGoogleSignInAttempt(buildNativeAuthRelayUrl(redirectPath), googleAttemptId);
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: redirectUrl,
        skipBrowserRedirect: true,
        queryParams: getOAuthQueryParams(provider),
      },
    });

    if (error) {
      return { error };
    }

    if (!data?.url) {
      clearPendingNativeAuthFlow();
      return { error: new Error(`Unable to start ${provider === 'apple' ? 'Apple' : 'Google'} sign-in.`) };
    }

    let browserFinishedListener: { remove: () => Promise<void> } | null = null;
    try {
      markNativeAuthFlowPending();
      browserFinishedListener = await Browser.addListener('browserFinished', async () => {
        try {
          const { data: sessionData } = await supabase.auth.getSession();
          if (googleAttemptId && (!sessionData.session?.user || isAnonymousUser(sessionData.session.user))) {
            trackGoogleSignInOutcome('returned_without_session', 'browser', googleAttemptId);
          }
          if (!sessionData.session?.user || typeof window === 'undefined') return;

          await navigateAfterAuth(normalizePostAuthPath(redirectPath));
        } catch (sessionError) {
          console.error('Native OAuth session check after browser close failed:', sessionError);
        } finally {
          clearPendingNativeAuthFlow();
          await browserFinishedListener?.remove().catch(() => undefined);
          browserFinishedListener = null;
        }
      });

      await Browser.open({ url: data.url });
      if (googleAttemptId) trackGoogleSignInOutcome('browser_opened', 'browser', googleAttemptId);
    } catch (openError) {
      clearPendingNativeAuthFlow();
      await browserFinishedListener?.remove().catch(() => undefined);
      void trackAuthDiagnosticEvent('native_oauth_browser_open_failed', {
        stage: 'native_oauth_browser_open',
        provider,
        error_message: getAuthDiagnosticErrorMessage(openError),
      });
      return { error: openError };
    }

    return { error: null };
  }, []);

  const startNativeOAuthProviderLink = useCallback(async (
    provider: OAuthProvider,
    redirectPath?: string,
  ): Promise<{ error: unknown; connected: boolean }> => {
    if (!user || !isAnonymousUser(user)) {
      return {
        error: new Error('This account is already connected.'),
        connected: false,
      };
    }

    clearPendingNativeAuthFlow();
    const redirectUrl = buildNativeAuthRelayUrl(redirectPath);
    const { data, error } = await supabase.auth.linkIdentity({
      provider,
      options: {
        redirectTo: redirectUrl,
        skipBrowserRedirect: true,
        queryParams: getOAuthQueryParams(provider),
      },
    });

    if (error) return { error, connected: false };
    if (!data?.url) {
      return {
        error: new Error(`Unable to connect ${provider === 'apple' ? 'Apple' : 'Google'}.`),
        connected: false,
      };
    }

    let browserFinishedListener: { remove: () => Promise<void> } | null = null;
    try {
      markNativeAuthFlowPending();
      markPendingIdentityLink(user.id, provider);
      browserFinishedListener = await Browser.addListener('browserFinished', async () => {
        try {
          const { data: sessionData } = await supabase.auth.getSession();
          const currentUser = sessionData.session?.user;
          if (
            !currentUser ||
            currentUser.id !== user.id ||
            isAnonymousUser(currentUser) ||
            typeof window === 'undefined'
          ) {
            return;
          }

          const finalized = await finalizeNativeGuestAccount();
          if (finalized.error) {
            throw finalized.error;
          }

          clearNativeAccountCompletion(user.id);
          scheduleUserAcquisitionPersistence(user.id);
          setTrackingUserId(user.id, currentUser.email);
          // Same gate as the callback path. The link marker stays until the
          // event is sent, so a reload after a pending or failed opt-out check
          // still sends it as guest_upgrade.
          // navigateAfterAuth waits for this delivery (shared with a SIGNED_IN
          // one already running) before the hard navigation.
          void sendGuardedSignup({
            userId: user.id,
            method: provider,
            email: currentUser.email || undefined,
            flow: 'guest_upgrade',
          }).then((sent) => {
            if (sent) clearPendingIdentityLink();
          }).catch(() => undefined);

          await navigateAfterAuth(normalizePostAuthPath(redirectPath));
        } catch (sessionError) {
          console.error('Native identity-link check after browser close failed:', sessionError);
        } finally {
          clearPendingNativeAuthFlow();
          await browserFinishedListener?.remove().catch(() => undefined);
          browserFinishedListener = null;
        }
      });

      await Browser.open({ url: data.url });
    } catch (openError) {
      clearPendingNativeAuthFlow();
      clearPendingIdentityLink();
      await browserFinishedListener?.remove().catch(() => undefined);
      return { error: openError, connected: false };
    }

    return { error: null, connected: false };
  }, [finalizeNativeGuestAccount, user]);

  const connectAnonymousWithProvider = useCallback(async (
    provider: OAuthProvider,
    redirectPath = '/complete-account',
    options: AnonymousProviderConnectionOptions = {},
  ): Promise<AnonymousProviderConnectionResult> => {
    clearNativeOAuthError();
    if (!isNativeRuntime || !user || !isAnonymousUser(user)) {
      return {
        error: new Error('Account connection is only available during mobile guest onboarding.'),
        connected: false,
      };
    }

    const finishLink = async (linkedUser: User | null) => {
      if (!linkedUser || linkedUser.id !== user.id) {
        return {
          error: new Error('We could not safely connect that account. Your membership is still on this device.'),
          connected: false,
        };
      }

      const finalized = await finalizeNativeGuestAccount();
      if (finalized.error) {
        return { error: finalized.error, connected: false };
      }

      clearNativeAccountCompletion(user.id);
      scheduleUserAcquisitionPersistence(user.id);
      setTrackingUserId(user.id, linkedUser.email);
      void sendGuardedSignup({
        userId: user.id,
        method: provider,
        email: linkedUser.email || undefined,
        flow: 'guest_upgrade',
      });
      return { error: null, connected: true };
    };

    try {
      if (provider === 'apple' && Capacitor.getPlatform() === 'ios') {
        const result = await signInWithNativeApple();
        const { data, error } = await supabase.auth.linkIdentity({
          provider: 'apple',
          token: result.identityToken,
          nonce: result.nonce,
        });
        return error ? { error, connected: false } : await finishLink(data.user);
      }

      if (provider === 'google' && isNativeGoogleSignInConfigured()) {
        const result = await signInWithNativeGoogle({ forceAccountSelection: true });
        const { data, error } = await supabase.auth.linkIdentity({
          provider: 'google',
          token: result.idToken,
          nonce: result.nonce,
          ...(result.accessToken ? { access_token: result.accessToken } : {}),
        });
        if (!error) {
          return finishLink(data.user);
        }

        if (options.allowExistingAccountSignIn && isIdentityAlreadyLinkedError(error)) {
          const exchange = await completeGoogleServerExchange({
            idToken: result.idToken,
            nonce: result.nonce,
            existingOnly: true,
            email: result.email ?? null,
            givenName: result.givenName ?? null,
            familyName: result.familyName ?? null,
          });

          if (exchange.error) {
            return { error: exchange.error, connected: false };
          }

          clearNativeAccountCompletion(user.id);
          trackNativeIdTokenAuthSuccess('google', exchange);
          return {
            error: null,
            connected: true,
            signedInExistingAccount: true,
          };
        }

        return { error, connected: false };
      }
    } catch (error) {
      if (provider === 'google' && isNativeGoogleSignInCanceled(error)) {
        return { error: null, connected: false };
      }

      if (getAuthDiagnosticErrorMessage(error).toLowerCase().includes('cancel')) {
        return { error: null, connected: false };
      }

      // A native provider sheet that cannot open falls through to the
      // authenticated browser-link flow below.
    }

    return startNativeOAuthProviderLink(provider, redirectPath);
  }, [finalizeNativeGuestAccount, isNativeRuntime, startNativeOAuthProviderLink, user]);

  const startNativeAppleSignIn = useCallback(async (redirectPath?: string) => {
    try {
      const result = await signInWithNativeApple();
      const exchange = await completeAppleServerExchange({
        identityToken: result.identityToken,
        authorizationCode: result.authorizationCode ?? null,
        nonce: result.nonce,
        email: result.email ?? null,
        givenName: result.givenName ?? null,
        familyName: result.familyName ?? null,
        user: result.user ?? null,
      });

      if (exchange.error) {
        return { error: exchange.error };
      }

      trackAppleAuthSuccess(exchange);
      void applyAppleProfileMetadata(result);
      return { error: null };
    } catch (error) {
      const message = getAuthDiagnosticErrorMessage(error);

      if (message.includes('anchor_unresolved')) {
        void trackAuthDiagnosticEvent('native_apple_signin_failed', {
          stage: 'native_apple_signin',
          provider: 'apple',
          error_message: message,
        });

        // No window could present the native sheet; the browser relay gives
        // the same session a second path instead of a dead end, mirroring
        // the Google fallback below.
        return startNativeOAuthProviderSignIn('apple', redirectPath);
      }

      return {
        error: error instanceof Error ? error : new Error('Apple sign-in could not be completed.'),
      };
    }
  }, [startNativeOAuthProviderSignIn]);

  const startNativeGoogleSignIn = useCallback(async (redirectPath?: string) => {
    const attemptId = getGoogleSignInAttemptId();
    trackGoogleSignInOutcome('launch_requested', 'native_sdk', attemptId);
    try {
      const result = await signInWithNativeGoogle({ forceAccountSelection: true });
      const exchange = await completeGoogleServerExchange({
        idToken: result.idToken,
        nonce: result.nonce,
        email: result.email ?? null,
        givenName: result.givenName ?? null,
        familyName: result.familyName ?? null,
      });

      if (exchange.error) {
        trackGoogleSignInOutcome('failed', 'session_exchange', attemptId);
        return { error: exchange.error };
      }

      trackGoogleSignInOutcome('completed', 'session_exchange', attemptId);
      trackNativeIdTokenAuthSuccess('google', exchange);
      return { error: null };
    } catch (error) {
      if (isNativeGoogleSignInCanceled(error)) {
        trackGoogleSignInOutcome('cancelled', 'native_sdk', attemptId);
        return { error: null };
      }

      void trackAuthDiagnosticEvent('native_google_signin_failed', {
        stage: 'native_google_signin',
        provider: 'google',
        error_message: getAuthDiagnosticErrorMessage(error),
      });

      trackGoogleSignInOutcome('fallback_started', 'native_sdk', attemptId);

      // Keep browser OAuth as recovery for SDK failures.
      return startNativeOAuthProviderSignIn('google', redirectPath);
    }
  }, [startNativeOAuthProviderSignIn]);

  const signInWithOAuthProvider = useCallback(async (provider: OAuthProvider, redirectPath?: string) => {
    if (provider !== 'google') clearGoogleSignInAttempt();
    clearNativeOAuthError();

    if (isNativeRuntime && provider === 'apple' && Capacitor.getPlatform() === 'ios') {
      return startNativeAppleSignIn(redirectPath);
    }

    if (isNativeRuntime && provider === 'google' && isNativeGoogleSignInConfigured()) {
      return startNativeGoogleSignIn(redirectPath);
    }

    if (provider === 'apple') {
      try {
        const result = await signInWithWebApple();
        const exchange = await completeAppleServerExchange({
          identityToken: result.identityToken,
          authorizationCode: null,
          nonce: result.nonce,
          email: result.email ?? null,
          givenName: result.givenName ?? null,
          familyName: result.familyName ?? null,
          user: null,
        });

        if (exchange.error) {
          return { error: exchange.error };
        }

        trackAppleAuthSuccess(exchange);
        void applyAppleProfileMetadata(result);

        return { error: null };
      } catch (error) {
        return {
          error: error instanceof Error ? error : new Error('Apple sign-in could not be completed.'),
        };
      }
    }

    if (isNativeRuntime) {
      return startNativeOAuthProviderSignIn(provider, redirectPath);
    }

    const redirectUrl = withGoogleSignInAttempt(
      buildWebAuthCallbackUrl(redirectPath),
      provider === 'google' ? getGoogleSignInAttemptId() : undefined,
    );

    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: redirectUrl,
        queryParams: getOAuthQueryParams(provider),
      },
    });
    return { error };
  }, [isNativeRuntime, startNativeAppleSignIn, startNativeGoogleSignIn, startNativeOAuthProviderSignIn]);

  const signInWithGoogle = useCallback(async (redirectPath?: string) => {
    const attemptId = beginGoogleSignInAttempt();
    if (!isNativeRuntime || !isNativeGoogleSignInConfigured()) {
      trackGoogleSignInOutcome('launch_requested', 'browser', attemptId);
    }
    try {
      const result = await signInWithOAuthProvider('google', redirectPath);
      if (result.error) trackGoogleSignInOutcome('failed', 'start', attemptId);
      return result;
    } catch (error) {
      trackGoogleSignInOutcome('failed', 'start', attemptId);
      throw error;
    }
  }, [isNativeRuntime, signInWithOAuthProvider]);

  const signInWithApple = useCallback(async (redirectPath?: string) => {
    return signInWithOAuthProvider('apple', redirectPath);
  }, [signInWithOAuthProvider]);

  const resendVerificationEmail = useCallback(async (email: string, redirectPath?: string) => {
    const redirectUrl = isNativeRuntime
      ? buildNativeAuthRelayUrl(redirectPath)
      : buildWebAuthCallbackUrl(redirectPath, {
          signup: true,
          signupRef: getPendingSignupRef(normalizeAuthEmail(email)),
        });

    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: normalizeAuthEmail(email),
      options: {
        emailRedirectTo: redirectUrl,
      },
    });

    return { error };
  }, [isNativeRuntime]);

  const updateEmail = useCallback(async (email: string) => {
    const redirectPath = '/settings?email_changed=1';
    const redirectUrl = isNativeRuntime
      ? buildNativeAuthRelayUrl(redirectPath)
      : buildWebAuthCallbackUrl(redirectPath);
    const { error } = await supabase.auth.updateUser(
      { email: normalizeAuthEmail(email) },
      { emailRedirectTo: redirectUrl },
    );
    return { error };
  }, [isNativeRuntime]);

  const updatePassword = useCallback(async (password: string) => {
    const { error } = await supabase.auth.updateUser({
      password,
      data: {
        requires_password_setup: false,
        password_setup_completed_at: new Date().toISOString(),
      },
    });
    if (!error) {
      recoveryUserIdRef.current = null;
      setIsRecovering(false);
    }
    return { error };
  }, []);

  // Memoize so consumers only re-render when auth state actually changes,
  // not on every provider render (e.g. hourly token refreshes).
  const value = useMemo(() => ({
    user,
    session,
    profile,
    loading,
    isLoading: loading,
    profileLoading,
    profileLoadFailed,
    isRecovering,
    authStartupFailed,
    retryAuthStartup,
    startAnonymousSession,
    connectAnonymousWithProvider,
    requestAnonymousEmailVerification,
    verifyAnonymousEmail,
    refreshAnonymousEmailVerification,
    requestEmailSignInCode,
    verifyEmailSignInCode,
    signIn,
    signUp,
    signInWithGoogle,
    signInWithApple,
    resendVerificationEmail,
    signOut,
    resetPassword,
    updateEmail,
    updatePassword,
    refreshProfile,
  }), [
    user,
    session,
    profile,
    loading,
    profileLoading,
    profileLoadFailed,
    isRecovering,
    authStartupFailed,
    retryAuthStartup,
    startAnonymousSession,
    connectAnonymousWithProvider,
    requestAnonymousEmailVerification,
    verifyAnonymousEmail,
    refreshAnonymousEmailVerification,
    requestEmailSignInCode,
    verifyEmailSignInCode,
    signIn,
    signUp,
    signInWithGoogle,
    signInWithApple,
    resendVerificationEmail,
    signOut,
    resetPassword,
    updateEmail,
    updatePassword,
    refreshProfile,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
